import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';

import { api } from '@/shared/api/http';
import type { CursorPage, FileRef, PagedResult, UserRef } from '@/shared/api/types';

/* ------------------------------------------------------------------ wire types (docs/modules/crm.md) */

export type EntityType = 'lead' | 'deal' | 'contact' | 'company' | 'quote' | 'invoice' | 'item';
/** Leads, deals, contacts and companies: the records with activities, boards or lists of part 1. */
export type RecordType = 'lead' | 'deal' | 'contact' | 'company';
/** The records activities belong to: the four of part 1 and the items of user-defined types («Свои сущности»). */
export type ActivityOwnerType = RecordType | 'item';
/** The kinds of records an item may be linked to. */
export type LinkKind = 'lead' | 'deal' | 'item';
export const LINK_KINDS: readonly LinkKind[] = ['deal', 'lead', 'item'];
/** Quotes and invoices. */
export type DocumentType = 'quote' | 'invoice';
export type QuoteStatus = 'draft' | 'sent' | 'accepted' | 'declined';
export type InvoiceStatus = 'draft' | 'issued' | 'paid' | 'overdue' | 'cancelled';
/** `link` (a web address), `address` (free text up to 500 characters) and `file` (ids of uploaded files) came with wave 11.2. */
export type CustomFieldType = 'string' | 'number' | 'money' | 'date' | 'list' | 'boolean' | 'user' | 'link' | 'address' | 'file';
export type ImportStatus = 'queued' | 'running' | 'completed' | 'failed';
export type ImportDuplicateMode = 'skip' | 'merge' | 'create';
export type StageSemantic = 'inProgress' | 'won' | 'lost';
export type LeadStatusSemantic = 'inProgress' | 'converted' | 'junk';
export type CrmRole = 'none' | 'viewer' | 'manager' | 'admin';
export type CrmScope = 'none' | 'own' | 'department' | 'all';
export type ActivityType = 'call' | 'meeting' | 'email' | 'todo';
export type ActivityDirection = 'none' | 'incoming' | 'outgoing';
export type ContactType = 'client' | 'supplier' | 'partner' | 'other';
export type CompanyType = 'client' | 'supplier' | 'partner' | 'competitor' | 'other';
export type TimelineKind =
  | 'created'
  | 'comment'
  | 'activityCompleted'
  | 'stage'
  | 'status'
  | 'field'
  | 'responsible'
  | 'observers'
  | 'converted'
  | 'link'
  | 'documentStatus'
  | 'products'
  | 'merged'
  | 'intake'
  | 'conversation'
  | 'trash';

/** One message of a conversation attached to a timeline (`data.messages` of a `conversation` entry, JSON). */
export interface TranscriptMessage {
  sentAt: string;
  author: string;
  fromClient: boolean;
  text: string;
}

export const ENTITY_TYPES: readonly EntityType[] = ['lead', 'deal', 'contact', 'company', 'quote', 'invoice', 'item'];
/** The types with a fixed place in the CRM (custom fields and roles of «Свои сущности» are per type). */
export const FIXED_ENTITY_TYPES: readonly EntityType[] = ['lead', 'deal', 'contact', 'company', 'quote', 'invoice'];
export const RECORD_TYPES: readonly RecordType[] = ['lead', 'deal', 'contact', 'company'];
export const DOCUMENT_TYPES: readonly DocumentType[] = ['quote', 'invoice'];
export const QUOTE_STATUSES: readonly QuoteStatus[] = ['draft', 'sent', 'accepted', 'declined'];
export const INVOICE_STATUSES: readonly InvoiceStatus[] = ['draft', 'issued', 'paid', 'overdue', 'cancelled'];

/** Activities belong to these types (not to quotes and invoices). */
export const hasActivities = (type: EntityType): type is ActivityOwnerType =>
  type === 'item' || (RECORD_TYPES as readonly string[]).includes(type);

/** Plural route segment of an entity type (`/api/crm/<route>`, SPA `/crm/<route>`). */
export const ROUTE: Record<EntityType, string> = {
  lead: 'leads',
  deal: 'deals',
  contact: 'contacts',
  company: 'companies',
  quote: 'quotes',
  invoice: 'invoices',
  item: 'items',
};

export interface Stage {
  id: string;
  pipelineId: string;
  name: string;
  color: string;
  semantic: StageSemantic;
  sortOrder: number;
  /**
   * The fields a record must have to enter the stage (docs/modules/crm.md "Stage requirements"): keys of standard fields
   * (`amount`, `company`…) and custom field codes (`cf_…`).
   */
  requiredFields?: string[];
}

export interface Pipeline {
  id: string;
  name: string;
  isDefault: boolean;
  sortOrder: number;
  stages: Stage[];
  /** What the caller may do with the deals of the pipeline (the pipeline matrix); absent: everything their role allows. */
  canEdit?: boolean;
  canDelete?: boolean;
}

/** One row of the pipeline matrix (docs/modules/crm.md "Pipeline permissions"): what a role may do with the pipeline's deals. */
export interface PipelineAccess {
  pipelineId: string;
  role: CrmRole;
  canView: boolean;
  canEdit: boolean;
  canDelete: boolean;
}

export interface LeadStatus {
  id: string;
  name: string;
  color: string;
  semantic: LeadStatusSemantic;
  sortOrder: number;
}

export interface ListItem {
  id: string;
  name: string;
  sortOrder: number;
}

export interface Currency {
  code: string;
  rate: number;
  isBase: boolean;
  sortOrder: number;
}

export interface EntityAccess {
  scope: CrmScope;
  canCreate: boolean;
}

export interface CustomFieldOption {
  id: string;
  label: string;
}

/** A custom field definition (values live in the records' `customFields` under `code`). */
export interface CustomField {
  id: string;
  entityType: EntityType;
  code: string;
  label: string;
  type: CustomFieldType;
  isRequired: boolean;
  isMultiple: boolean;
  options: CustomFieldOption[];
  currencyCode: string | null;
  showInList: boolean;
  showInFilter: boolean;
  sortOrder: number;
  /** The user-defined entity type of an `item` field. */
  dynamicTypeId?: string | null;
}

/** What a user-defined entity type offers (docs/modules/bizproc.md §10.1). */
export interface DynamicTypeFeatures {
  pipelines: boolean;
  client: boolean;
  observers: boolean;
  amount: boolean;
  /** Product rows: the amount of an item is their total (needs `amount`). */
  products: boolean;
  dates: boolean;
  links: LinkKind[];
  automation: boolean;
  processes: boolean;
  showInMenu: boolean;
}

export interface TypeScope {
  role: CrmRole;
  scope: CrmScope;
}

/** A user-defined entity type («Свои сущности») with its pipelines; `scopes` come for administrators only. */
export interface DynamicType {
  id: string;
  code: string;
  title: string;
  itemTitle: string;
  icon: string;
  color: string;
  sortOrder: number;
  features: DynamicTypeFeatures;
  pipelines: Pipeline[];
  scopes: TypeScope[];
}

/** A type for the caller: their access to its items; administrators also get the counts. */
export interface DynamicTypeAccess {
  type: DynamicType;
  access: EntityAccess;
  itemCount?: number | null;
  stageItems?: Record<string, number> | null;
}

/** Settings of quotes and invoices; `seller` holds our requisites printed on the documents. */
export interface DocumentSettings {
  quotePrefix: string;
  invoicePrefix: string;
  invoicePaidWinsDeal: boolean;
  seller: Record<string, string>;
}

/** `GET /api/crm/me`: role, access per entity type and the configuration every screen needs. */
export interface CrmMe {
  role: CrmRole;
  isAdmin: boolean;
  access: Record<EntityType, EntityAccess>;
  pipelines: Pipeline[];
  leadStatuses: LeadStatus[];
  sources: ListItem[];
  lossReasons: ListItem[];
  currencies: Currency[];
  baseCurrency: string;
  customFields: CustomField[];
  documents: DocumentSettings;
  /** The user-defined entity types the caller may use (none without a CRM role). */
  dynamicTypes?: DynamicTypeAccess[] | null;
  /** Whether the caller's role may export lists (`crm.export`); absent in answers of an older server (then: allowed). */
  canExport?: boolean;
  /**
   * «Use leads» (docs/modules/crm.md "Use leads"): `false` while the lead functionality is switched off — the menu, the CRM start
   * and the «create» buttons have no leads, and what would create a lead creates a contact and a deal. Absent in answers of an
   * older server (then: on).
   */
  useLeads?: boolean;
}

export interface ContactPoint {
  kind: string;
  value: string;
}

export interface CompanyRef {
  id: string;
  title: string;
}

export interface ContactRef {
  id: string;
  fullName: string;
  position: string | null;
  isPrimary: boolean;
}

export interface RecordRef {
  type: EntityType;
  id: string;
  title: string;
  subtitle?: string | null;
}

export interface RecordPermissions {
  edit: boolean;
  delete: boolean;
  work: boolean;
}

export interface ActivitySummary {
  open: number;
  nextDeadline: string | null;
  hasOverdue: boolean;
}

export interface LeadListItem {
  id: string;
  title: string;
  personName: string | null;
  companyTitle: string | null;
  phones: ContactPoint[];
  emails: ContactPoint[];
  statusId: string;
  statusSemantic: LeadStatusSemantic;
  sourceId: string | null;
  amount: number | null;
  currencyCode: string;
  responsible: UserRef | null;
  createdAt: string;
  updatedAt: string;
  statusChangedAt: string;
  activities: ActivitySummary;
  boardRank: number;
  canEdit: boolean;
  customFields: Record<string, unknown>;
}

export interface LeadDetail {
  id: string;
  title: string;
  firstName: string | null;
  lastName: string | null;
  secondName: string | null;
  companyTitle: string | null;
  position: string | null;
  phones: ContactPoint[];
  emails: ContactPoint[];
  address: string | null;
  statusId: string;
  statusSemantic: LeadStatusSemantic;
  statusChangedAt: string;
  sourceId: string | null;
  sourceDescription: string | null;
  amount: number | null;
  currencyCode: string;
  description: string | null;
  responsible: UserRef | null;
  observers: UserRef[];
  createdBy: UserRef | null;
  createdAt: string;
  updatedAt: string;
  convertedAt: string | null;
  convertedDeal: RecordRef | null;
  convertedContact: RecordRef | null;
  convertedCompany: RecordRef | null;
  customFields: Record<string, unknown>;
  /** The files the record's `file` custom fields reference, with signed links (resolve names and downloads from here). */
  files?: FileRef[];
  activities: ActivitySummary;
  permissions: RecordPermissions;
}

export interface DealListItem {
  id: string;
  title: string;
  pipelineId: string;
  stageId: string;
  stageSemantic: StageSemantic;
  amount: number | null;
  currencyCode: string;
  probability: number | null;
  expectedCloseDate: string | null;
  company: CompanyRef | null;
  contact: ContactRef | null;
  sourceId: string | null;
  responsible: UserRef | null;
  createdAt: string;
  updatedAt: string;
  stageChangedAt: string;
  closedAt: string | null;
  activities: ActivitySummary;
  boardRank: number;
  canEdit: boolean;
  customFields: Record<string, unknown>;
}

export interface DealDetail {
  id: string;
  title: string;
  pipelineId: string;
  stageId: string;
  stageSemantic: StageSemantic;
  stageChangedAt: string;
  amount: number | null;
  currencyCode: string;
  probability: number | null;
  expectedCloseDate: string | null;
  company: CompanyRef | null;
  contacts: ContactRef[];
  sourceId: string | null;
  lossReasonId: string | null;
  lossComment: string | null;
  closedAt: string | null;
  description: string | null;
  responsible: UserRef | null;
  observers: UserRef[];
  createdBy: UserRef | null;
  createdAt: string;
  updatedAt: string;
  lead: RecordRef | null;
  customFields: Record<string, unknown>;
  /** The files the record's `file` custom fields reference, with signed links (resolve names and downloads from here). */
  files?: FileRef[];
  activities: ActivitySummary;
  permissions: RecordPermissions;
  /** Product rows of the deal: with rows the amount is their total (read-only). */
  productCount: number;
}

/** An item of a user-defined entity type in a list or on its board. */
export interface ItemListItem {
  id: string;
  typeId: string;
  number: number;
  title: string;
  pipelineId: string;
  stageId: string;
  stageSemantic: StageSemantic;
  amount: number | null;
  currencyCode: string;
  beginDate: string | null;
  closeDate: string | null;
  company: CompanyRef | null;
  contact: ContactRef | null;
  responsible: UserRef | null;
  createdAt: string;
  updatedAt: string;
  stageChangedAt: string;
  closedAt: string | null;
  activities: ActivitySummary;
  boardRank: number;
  canEdit: boolean;
  customFields: Record<string, unknown>;
}

/** A record an item is linked to (`out`) or that points at the item (`in`); `hidden`: the caller may not see it. */
export interface ItemLink {
  id: string;
  direction: 'out' | 'in';
  kind: LinkKind;
  recordId: string;
  title: string;
  subtitle: string | null;
  typeId: string | null;
  typeCode: string | null;
  hidden: boolean;
  canRemove: boolean;
}

export interface ItemDetail {
  id: string;
  typeId: string;
  number: number;
  title: string;
  pipelineId: string;
  stageId: string;
  stageSemantic: StageSemantic;
  stageChangedAt: string;
  amount: number | null;
  currencyCode: string;
  beginDate: string | null;
  closeDate: string | null;
  company: CompanyRef | null;
  contacts: ContactRef[];
  closedAt: string | null;
  description: string | null;
  responsible: UserRef | null;
  observers: UserRef[];
  createdBy: UserRef | null;
  createdAt: string;
  updatedAt: string;
  customFields: Record<string, unknown>;
  /** The files the record's `file` custom fields reference, with signed links (resolve names and downloads from here). */
  files?: FileRef[];
  activities: ActivitySummary;
  permissions: RecordPermissions;
  links: ItemLink[];
  /** Product rows of the item: with rows the amount is their total (read-only). */
  productCount: number;
}

/** An item linked to a lead or a deal (their cards list them). */
export interface LinkedItem {
  linkId: string;
  itemId: string;
  typeId: string;
  typeCode: string;
  typeTitle: string;
  icon: string;
  number: number;
  title: string;
  stage: string | null;
  stageColor: string | null;
  stageSemantic: StageSemantic;
}

export interface ContactListItem {
  id: string;
  fullName: string;
  position: string | null;
  phones: ContactPoint[];
  emails: ContactPoint[];
  company: CompanyRef | null;
  type: ContactType;
  sourceId: string | null;
  responsible: UserRef | null;
  createdAt: string;
  updatedAt: string;
  activities: ActivitySummary;
  canEdit: boolean;
  customFields: Record<string, unknown>;
}

export interface ContactDetail {
  id: string;
  fullName: string;
  firstName: string | null;
  lastName: string | null;
  secondName: string | null;
  position: string | null;
  phones: ContactPoint[];
  emails: ContactPoint[];
  messengers: ContactPoint[];
  address: string | null;
  birthday: string | null;
  type: ContactType;
  sourceId: string | null;
  description: string | null;
  companies: CompanyRef[];
  responsible: UserRef | null;
  createdBy: UserRef | null;
  createdAt: string;
  updatedAt: string;
  lead: RecordRef | null;
  customFields: Record<string, unknown>;
  /** The files the record's `file` custom fields reference, with signed links (resolve names and downloads from here). */
  files?: FileRef[];
  activities: ActivitySummary;
  permissions: RecordPermissions;
}

export interface CompanyListItem {
  id: string;
  title: string;
  industry: string | null;
  type: CompanyType;
  employees: string | null;
  revenue: number | null;
  currencyCode: string;
  phones: ContactPoint[];
  emails: ContactPoint[];
  websites: ContactPoint[];
  responsible: UserRef | null;
  createdAt: string;
  updatedAt: string;
  contactCount: number;
  activities: ActivitySummary;
  canEdit: boolean;
  customFields: Record<string, unknown>;
}

export interface CompanyDetail {
  id: string;
  title: string;
  industry: string | null;
  type: CompanyType;
  employees: string | null;
  revenue: number | null;
  currencyCode: string;
  phones: ContactPoint[];
  emails: ContactPoint[];
  websites: ContactPoint[];
  address: string | null;
  requisites: Record<string, string>;
  sourceId: string | null;
  description: string | null;
  responsible: UserRef | null;
  createdBy: UserRef | null;
  createdAt: string;
  updatedAt: string;
  lead: RecordRef | null;
  customFields: Record<string, unknown>;
  /** The files the record's `file` custom fields reference, with signed links (resolve names and downloads from here). */
  files?: FileRef[];
  activities: ActivitySummary;
  permissions: RecordPermissions;
}

/** What a linked company is to the one whose card lists it (wire values; texts in `companyLinks.relations.*`). */
export type CompanyRelation = 'parent' | 'subsidiary' | 'partner' | 'other';

/** A company linked to another one (listed on both cards); `hidden`: the caller may not see the company on the other end. */
export interface CompanyLink {
  id: string;
  companyId: string;
  title: string;
  type: CompanyType | null;
  industry: string | null;
  relation: CompanyRelation | null;
  hidden: boolean;
  canEdit: boolean;
  createdAt: string;
}

export interface BoardColumn<T> {
  id: string;
  count: number;
  sum: number;
  items: T[];
}

export interface Board<T> {
  baseCurrency: string;
  columns: BoardColumn<T>[];
}

/** Why a bulk action skipped a record: an error code and, for stage requirements, the keys of the fields it lacks. */
export interface BulkSkipped {
  id: string;
  code: string;
  fields?: string[] | null;
}

export interface BulkResult {
  updated: number;
  skipped: string[];
  reasons?: BulkSkipped[] | null;
}

/** An entry of the CRM trash (docs/modules/crm.md "Trash"). */
export interface TrashEntry {
  type: EntityType;
  id: string;
  typeId: string | null;
  typeTitle: string | null;
  title: string;
  subtitle: string | null;
  responsible: UserRef | null;
  deletedBy: UserRef | null;
  deletedAt: string;
  purgeAt: string | null;
  canRestore: boolean;
  canPurge: boolean;
}

export interface TrashPage {
  items: TrashEntry[];
  page: number;
  pageSize: number;
  total: number;
  retentionDays: number;
  canPurge: boolean;
  counts: Partial<Record<EntityType, number>>;
}

export interface RestoreResult {
  type: EntityType;
  id: string;
  typeId: string | null;
  reassigned: boolean;
  duplicates: Duplicate[];
  hiddenDuplicates: number;
}

export interface StageHistoryEntry {
  stageId: string;
  pipelineId: string;
  semantic: StageSemantic;
  enteredAt: string;
  leftAt: string | null;
  seconds: number;
  actor: UserRef | null;
}

export interface Activity {
  id: string;
  type: ActivityType;
  direction: ActivityDirection;
  subject: string;
  descriptionHtml: string;
  deadline: string | null;
  endAt: string | null;
  location: string | null;
  owner: RecordRef;
  responsible: UserRef | null;
  isCompleted: boolean;
  completedAt: string | null;
  reminderMinutes: number | null;
  createdBy: UserRef | null;
  createdAt: string;
  isOverdue: boolean;
  canEdit: boolean;
  /** Employees taking part besides the responsible person (meetings: invited in the calendar). */
  participants: UserRef[];
  /** The meeting is shown in the responsible person's calendar. */
  inCalendar: boolean;
}

export interface ActivityCounters {
  overdue: number;
  today: number;
  upcoming: number;
}

export interface CrmComment {
  id: string;
  author: UserRef;
  bodyHtml: string;
  attachments: FileRef[];
  createdAt: string;
  editedAt: string | null;
  canEdit: boolean;
  canDelete: boolean;
}

export interface TimelineEntry {
  id: string;
  kind: TimelineKind;
  author: UserRef | null;
  createdAt: string;
  data: Record<string, string>;
  users: Record<string, UserRef>;
  comment: CrmComment | null;
  activity: Activity | null;
  /** Files a client sent with a request (`intake` entries; signed links). */
  files?: FileRef[];
}

export interface Duplicate {
  type: EntityType;
  id: string;
  title: string;
  subtitle: string | null;
  matchedBy: string[];
}

export interface ConversionPreview {
  deal: {
    title: string | null;
    pipelineId: string | null;
    stageId: string | null;
    amount: number | null;
    currencyCode: string | null;
  };
  contact: {
    existingId: string | null;
    firstName: string | null;
    lastName: string | null;
    secondName: string | null;
    position: string | null;
  } | null;
  company: { existingId: string | null; title: string | null } | null;
  contactDuplicates: Duplicate[];
  companyDuplicates: Duplicate[];
  hiddenDuplicates: number;
  canCreateDeal: boolean;
  canCreateContact: boolean;
  canCreateCompany: boolean;
}

export interface ConvertInput {
  deal?: {
    title?: string;
    pipelineId?: string;
    stageId?: string;
    amount?: number | null;
    currencyCode?: string;
  } | null;
  contact?: {
    existingId?: string | null;
    firstName?: string | null;
    lastName?: string | null;
    secondName?: string | null;
    position?: string | null;
  } | null;
  company?: { existingId?: string | null; title?: string | null } | null;
}

export interface ConversionResult {
  leadId: string;
  dealId: string | null;
  contactId: string | null;
  companyId: string | null;
}

/** Who sees a saved view: its author, the author's department, everybody with a CRM role. */
export type ViewScope = 'personal' | 'department' | 'all';

/**
 * A saved view of a list (wave 11.2: filter, sort and columns, personal or shared). The fields after `typeId` come with the
 * shared views; an older answer lacks them (the view is then the caller's own, without columns).
 */
export interface SavedFilter {
  id: string;
  entityType: EntityType;
  name: string;
  query: Record<string, string>;
  createdAt: string;
  /** Items: the user-defined entity type whose list the preset belongs to. */
  typeId?: string | null;
  scope?: ViewScope;
  departmentId?: string | null;
  departmentName?: string | null;
  /** The ids of the visible columns (null: the user's own choice). */
  columns?: string[] | null;
  /** The view a list opens with for people of this CRM role (administrators set it). */
  defaultRole?: Exclude<CrmRole, 'none'> | null;
  authorId?: string;
  authorName?: string | null;
  isMine?: boolean;
  canEdit?: boolean;
}

export interface UserSummary {
  openDeals: number;
  openAmount: number;
  baseCurrency: string;
  deals: DealListItem[];
  activities: Activity[];
  /** Deals they won this month (the viewer's calendar) and their unpaid (issued or overdue) invoices; sums in the base currency. */
  wonThisMonth: { count: number; sum: number };
  unpaidInvoices: { count: number; sum: number };
}

export interface RoleScopes {
  role: CrmRole;
  lead: CrmScope;
  deal: CrmScope;
  contact: CrmScope;
  company: CrmScope;
  quote: CrmScope;
  invoice: CrmScope;
  /** Whether the role may export lists (administrators always may); absent in answers of an older server (then: allowed). */
  canExport?: boolean;
}

export interface RoleAssignment {
  user: UserRef;
  role: CrmRole;
  assignedAt: string;
}

export interface CrmSettings {
  defaultRole: CrmRole;
  feedOnDealWon: boolean;
  /** «Use leads»: the lead functionality is on (the administrator's choice, else the default of the edition). */
  useLeads?: boolean;
  roles: RoleScopes[];
  assignments: RoleAssignment[];
  stageDeals: Record<string, number>;
  statusLeads: Record<string, number>;
  documents: DocumentSettings;
  intake: IntakeSettings;
  /** The rows of the pipeline matrix (roles without a row are not restricted). */
  pipelineAccess?: PipelineAccess[] | null;
}

/** What requests of clients from open lines and web forms become (docs/modules/crm.md "Lead intake"). */
export type IntakeMode = 'attach' | 'newLead';

/** Kinds of channels requests come through. */
export type IntakeSourceKind = 'openline' | 'webform' | 'other';

export interface IntakeSettings {
  mode: IntakeMode;
  responsible: UserRef | null;
  /** CRM source of each channel kind. */
  sources: Partial<Record<IntakeSourceKind, string>>;
}

/** Where automatic currency rates come from. */
export type RatesProvider = 'none' | 'nbu';

export interface CurrencyRates {
  provider: RatesProvider;
  autoUpdate: boolean;
  updatedAt: string | null;
  rateDate: string | null;
  checkedAt: string | null;
  error: string | null;
  providers: RatesProvider[];
  baseCurrency: string;
  currencies: Currency[];
}

export interface CurrencyRateHistory {
  date: string;
  rate: number;
  baseCurrency: string;
  /** `manual` or `nbu`. */
  source: string;
  recordedAt: string;
  recordedBy: UserRef | null;
}

/* ---------------------------------------------------------------- products, documents, tools (part 2) */

export interface ProductSection {
  id: string;
  parentId: string | null;
  name: string;
  sortOrder: number;
  productCount: number;
}

export interface Product {
  id: string;
  sectionId: string | null;
  name: string;
  sku: string | null;
  description: string | null;
  price: number;
  currencyCode: string;
  unit: string;
  vatRate: number | null;
  vatIncluded: boolean;
  isActive: boolean;
  image: FileRef | null;
  createdAt: string;
  updatedAt: string;
}

export interface RowAmounts {
  gross: number;
  discount: number;
  net: number;
  tax: number;
  total: number;
}

export interface ProductTotals {
  count: number;
  subtotal: number;
  discount: number;
  tax: number;
  total: number;
}

export interface ProductRow {
  id: string;
  productId: string | null;
  name: string;
  price: number;
  quantity: number;
  unit: string | null;
  discountPercent: number;
  taxRate: number | null;
  taxIncluded: boolean;
  sortOrder: number;
  amounts: RowAmounts;
}

export interface ProductRows {
  currencyCode: string;
  rows: ProductRow[];
  totals: ProductTotals;
}

/** A row being edited (no id; amounts computed on the client). */
export interface ProductRowInput {
  productId: string | null;
  name: string;
  price: number;
  quantity: number;
  unit: string | null;
  discountPercent: number;
  taxRate: number | null;
  taxIncluded: boolean;
}

export interface DocumentParty {
  id: string;
  title: string;
  subtitle: string | null;
  address: string | null;
  phone: string | null;
  email: string | null;
  requisites: Record<string, string>;
  /** The caller may not see the linked company or contact: only its name comes along. */
  hidden?: boolean;
}

export interface DocumentListItem {
  id: string;
  type: DocumentType;
  number: string;
  title: string;
  status: QuoteStatus | InvoiceStatus;
  issueDate: string;
  validUntil: string | null;
  dueDate: string | null;
  paidAt: string | null;
  currencyCode: string;
  amount: number;
  company: CompanyRef | null;
  contact: ContactRef | null;
  deal: RecordRef | null;
  responsible: UserRef | null;
  createdAt: string;
  updatedAt: string;
  statusChangedAt: string;
  customFields: Record<string, unknown>;
  canEdit: boolean;
}

export interface DocumentDetail {
  id: string;
  type: DocumentType;
  number: string;
  title: string;
  status: QuoteStatus | InvoiceStatus;
  statusChangedAt: string;
  issueDate: string;
  validUntil: string | null;
  dueDate: string | null;
  paidAt: string | null;
  currencyCode: string;
  subtotal: number;
  discountAmount: number;
  taxAmount: number;
  amount: number;
  deal: RecordRef | null;
  company: DocumentParty | null;
  contact: DocumentParty | null;
  quote: RecordRef | null;
  invoices: RecordRef[];
  terms: string | null;
  description: string | null;
  responsible: UserRef | null;
  createdBy: UserRef | null;
  createdAt: string;
  updatedAt: string;
  rows: ProductRow[];
  customFields: Record<string, unknown>;
  /** The files the record's `file` custom fields reference, with signed links (resolve names and downloads from here). */
  files?: FileRef[];
  permissions: RecordPermissions;
}

export interface FunnelStage {
  stageId: string;
  count: number;
  sum: number;
}

export interface Amount {
  count: number;
  sum: number;
}

export interface Dashboard {
  baseCurrency: string;
  pipelineId: string;
  from: string;
  to: string;
  funnel: FunnelStage[];
  won: Amount;
  wonBefore: Amount;
  open: Amount;
  leads: number;
  leadsBefore: number;
  leadsCreated90: number;
  leadsConverted90: number;
  dealsWon: number;
  dealsLost: number;
  invoicesUnpaid: Amount;
  invoicesOverdue: Amount;
  months: { month: string; count: number; sum: number }[];
  leaders: { user: UserRef; count: number; sum: number }[];
  activities: ActivityCounters;
  nextActivities: Activity[];
}

export interface DuplicateRecord {
  id: string;
  title: string;
  subtitle: string | null;
  phones: ContactPoint[];
  emails: ContactPoint[];
  responsible: UserRef | null;
  createdAt: string;
  deals: number;
  canEdit: boolean;
}

export interface DuplicateGroup {
  matchedBy: string[];
  records: DuplicateRecord[];
}

export interface ImportField {
  key: string;
  label: string;
  isCustom: boolean;
}

export interface ImportRowError {
  row: number;
  code: string;
  field: string | null;
  message: string;
  warning: boolean;
}

export interface ImportPreview {
  fileName: string;
  encoding: string;
  delimiter: string;
  hasHeader: boolean;
  headers: string[];
  rows: string[][];
  totalRows: number;
  fields: ImportField[];
  mapping: Record<string, string>;
  issues: ImportRowError[];
  duplicates: number;
  /** `csv` or `xlsx` (the first sheet of a workbook). */
  format: 'csv' | 'xlsx';
  /** Deals: the pipeline of rows without a known one. */
  pipelineId: string | null;
  /** Deals: the distinct texts of the stage column with the stage each goes to. */
  stageValues: ImportStageValue[];
}

/** A distinct text of the stage column (deals): `key` identifies it in the `stages` mapping. */
export interface ImportStageValue {
  value: string;
  key: string;
  count: number;
  stageId: string | null;
}

export interface ImportOptions {
  delimiter: string;
  encoding: string;
  hasHeader: boolean;
  duplicates: ImportDuplicateMode;
  responsibleId: string;
  format?: 'csv' | 'xlsx';
  pipelineId?: string | null;
  stages?: Record<string, string> | null;
  createCompanies?: boolean;
  createContacts?: boolean;
}

export interface ImportJob {
  id: string;
  entityType: RecordType;
  fileName: string;
  status: ImportStatus;
  totalRows: number;
  processedRows: number;
  created: number;
  updated: number;
  skipped: number;
  failed: number;
  errors: ImportRowError[];
  failureReason: string | null;
  options: ImportOptions;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
}

/** List query parameters (server-side filters, sort, page). */
export type ListQuery = Record<string, string | number | boolean | null | undefined>;

/** Cards a board column returns at once; "show more" asks for the next ones (`stageId`/`statusId` + `offset`). */
export const BOARD_PAGE = 100;

/** Rows an export holds at most (the API cuts longer lists and says so in `X-Orbita-Truncated`). */
export const EXPORT_LIMIT = 10_000;

/* ------------------------------------------------------------------ query keys */

export const crmKeys = {
  all: ['crm'] as const,
  me: ['crm', 'me'] as const,
  settings: ['crm', 'settings'] as const,
  lists: (type: EntityType) => ['crm', type, 'list'] as const,
  list: (type: EntityType, query: ListQuery) => ['crm', type, 'list', query] as const,
  boards: (type: EntityType) => ['crm', type, 'board'] as const,
  board: (type: EntityType, query: ListQuery) => ['crm', type, 'board', query] as const,
  details: (type: EntityType) => ['crm', type, 'detail'] as const,
  detail: (type: EntityType, id: string) => ['crm', type, 'detail', id] as const,
  timeline: (type: EntityType, id: string) => ['crm', 'timeline', type, id] as const,
  stageHistory: (id: string) => ['crm', 'deal', 'stage-history', id] as const,
  itemHistory: (id: string) => ['crm', 'item', 'stage-history', id] as const,
  linkedItems: (kind: LinkKind, id: string) => ['crm', 'linked-items', kind, id] as const,
  companyLinksAll: ['crm', 'company-links'] as const,
  companyLinks: (id: string) => ['crm', 'company-links', id] as const,
  types: ['crm', 'types'] as const,
  activities: ['crm', 'activities'] as const,
  activityList: (query: ListQuery) => ['crm', 'activities', 'list', query] as const,
  counters: ['crm', 'activities', 'counters'] as const,
  filters: (type: EntityType, typeId?: string | null) => ['crm', 'filters', type, typeId ?? null] as const,
  lookup: (type: EntityType, search: string) => ['crm', 'lookup', type, search] as const,
  duplicates: (type: EntityType, query: ListQuery) => ['crm', 'duplicates', type, query] as const,
  conversion: (id: string) => ['crm', 'lead', 'conversion', id] as const,
  summary: (userId: string) => ['crm', 'summary', userId] as const,
  sections: ['crm', 'catalog', 'sections'] as const,
  products: ['crm', 'catalog', 'products'] as const,
  productList: (query: ListQuery) => ['crm', 'catalog', 'products', query] as const,
  product: (id: string) => ['crm', 'catalog', 'product', id] as const,
  rows: (type: EntityType, id: string) => ['crm', 'rows', type, id] as const,
  dashboard: (query: ListQuery) => ['crm', 'dashboard', query] as const,
  duplicateGroups: (type: 'contact' | 'company') => ['crm', 'duplicate-groups', type] as const,
  imports: ['crm', 'imports'] as const,
  importJob: (id: string) => ['crm', 'imports', id] as const,
  trashAll: ['crm', 'trash'] as const,
  trash: (query: ListQuery) => ['crm', 'trash', query] as const,
  currencyRates: ['crm', 'currency-rates'] as const,
  currencyHistory: (code: string) => ['crm', 'currency-rates', 'history', code] as const,
};

/** Lists, boards and summaries of a type (and the profile tab) after a change. */
export function invalidateViews(queryClient: QueryClient, type: EntityType): void {
  void queryClient.invalidateQueries({ queryKey: crmKeys.lists(type) });
  void queryClient.invalidateQueries({ queryKey: crmKeys.boards(type) });
  void queryClient.invalidateQueries({ queryKey: ['crm', 'summary'] });
}

/** One record changed: its card, timeline and every view of its type. */
export function invalidateRecord(queryClient: QueryClient, type: EntityType, id: string): void {
  void queryClient.invalidateQueries({ queryKey: crmKeys.detail(type, id) });
  void queryClient.invalidateQueries({ queryKey: crmKeys.timeline(type, id) });
  void queryClient.invalidateQueries({ queryKey: crmKeys.rows(type, id) });
  if (type === 'deal') void queryClient.invalidateQueries({ queryKey: crmKeys.stageHistory(id) });
  if (type === 'item') void queryClient.invalidateQueries({ queryKey: crmKeys.itemHistory(id) });
  // A company and the companies linked to it list each other.
  if (type === 'company') void queryClient.invalidateQueries({ queryKey: crmKeys.companyLinksAll });
  // An item and the lead or deal it is linked to list each other.
  if (type === 'item' || type === 'lead' || type === 'deal') void queryClient.invalidateQueries({ queryKey: ['crm', 'linked-items'] });
  invalidateViews(queryClient, type);
  void queryClient.invalidateQueries({ queryKey: ['crm', 'dashboard'] });
}

/* ------------------------------------------------------------------ fetchers */

const base = '/crm';
const item = (type: EntityType, id: string, rest = '') =>
  `${base}/${ROUTE[type]}/${encodeURIComponent(id)}${rest}`;

export const crmApi = {
  me: (signal?: AbortSignal) => api.get<CrmMe>(`${base}/me`, { signal }),
  list: <T>(type: EntityType, query: ListQuery, signal?: AbortSignal) =>
    api.get<PagedResult<T>>(`${base}/${ROUTE[type]}`, { query, signal }),
  board: <T>(type: 'lead' | 'deal' | 'item', query: ListQuery, signal?: AbortSignal) =>
    api.get<Board<T>>(`${base}/${ROUTE[type]}/board`, { query, signal }),
  get: <T>(type: EntityType, id: string, signal?: AbortSignal) => api.get<T>(item(type, id), { signal }),
  create: <T>(type: EntityType, body: Record<string, unknown>) => api.post<T>(`${base}/${ROUTE[type]}`, body),
  update: <T>(type: EntityType, id: string, body: Record<string, unknown>) =>
    api.patch<T>(item(type, id), body),
  remove: (type: EntityType, id: string) => api.delete(item(type, id)),
  bulk: (
    type: EntityType,
    body: {
      action: string;
      ids: string[];
      responsibleId?: string;
      stageId?: string;
      statusId?: string;
      lossReasonId?: string;
      status?: string;
    },
  ) => api.post<BulkResult>(`${base}/${ROUTE[type]}/bulk`, body),
  moveDeal: (
    id: string,
    body: {
      stageId: string;
      lossReasonId?: string | null;
      lossComment?: string | null;
      afterId?: string | null;
      beforeId?: string | null;
    },
  ) => api.post<DealDetail>(item('deal', id, '/stage'), body),
  moveLead: (id: string, body: { statusId: string; afterId?: string | null; beforeId?: string | null }) =>
    api.post<LeadDetail>(item('lead', id, '/status'), body),
  stageHistory: (id: string, signal?: AbortSignal) =>
    api.get<StageHistoryEntry[]>(item('deal', id, '/stage-history'), { signal }),
  bulkItems: (body: {
    typeId: string;
    action: 'assign' | 'stage' | 'field' | 'delete';
    ids: string[];
    responsibleId?: string;
    stageId?: string;
    fieldCode?: string;
    fieldValue?: unknown;
  }) => api.post<BulkResult>(`${base}/items/bulk`, body),
  moveItem: (id: string, body: { stageId: string; afterId?: string | null; beforeId?: string | null }) =>
    api.post<ItemDetail>(item('item', id, '/stage'), body),
  itemHistory: (id: string, signal?: AbortSignal) =>
    api.get<StageHistoryEntry[]>(item('item', id, '/stage-history'), { signal }),
  addItemLink: (id: string, body: { kind: LinkKind; linkedId: string }) =>
    api.post<ItemDetail>(item('item', id, '/links'), body),
  removeItemLink: (id: string, linkId: string) =>
    api.delete<ItemDetail>(item('item', id, `/links/${encodeURIComponent(linkId)}`)),
  linkedItems: (kind: 'lead' | 'deal', id: string, signal?: AbortSignal) =>
    api.get<LinkedItem[]>(item(kind, id, '/items'), { signal }),
  companyLinks: (id: string, signal?: AbortSignal) => api.get<CompanyLink[]>(item('company', id, '/links'), { signal }),
  addCompanyLink: (id: string, body: { linkedId: string; relation: CompanyRelation | null }) =>
    api.post<CompanyLink>(item('company', id, '/links'), body),
  updateCompanyLink: (id: string, linkId: string, body: { relation: CompanyRelation | null }) =>
    api.patch<CompanyLink>(item('company', id, `/links/${encodeURIComponent(linkId)}`), body),
  removeCompanyLink: (id: string, linkId: string) =>
    api.delete(item('company', id, `/links/${encodeURIComponent(linkId)}`)),
  types: (signal?: AbortSignal) => api.get<DynamicTypeAccess[]>(`${base}/types`, { signal }),
  createType: (body: {
    code: string;
    title: string;
    itemTitle: string;
    icon: string;
    color: string;
    features: DynamicTypeFeatures;
  }) => api.post<DynamicTypeAccess>(`${base}/types`, body),
  updateType: (
    id: string,
    body: { title: string; itemTitle: string; icon: string; color: string; features: DynamicTypeFeatures },
  ) => api.put<DynamicTypeAccess>(`${base}/types/${id}`, body),
  removeType: (id: string) => api.delete(`${base}/types/${id}`),
  orderTypes: (ids: string[]) => api.put<DynamicTypeAccess[]>(`${base}/types/order`, { ids }),
  setTypeScopes: (id: string, body: { viewer: CrmScope | null; manager: CrmScope | null }) =>
    api.put<DynamicTypeAccess>(`${base}/types/${id}/scopes`, body),
  createTypePipeline: (typeId: string, name: string) =>
    api.post<Pipeline>(`${base}/types/${typeId}/pipelines`, { name }),
  updateTypePipeline: (typeId: string, id: string, body: { name: string; isDefault?: boolean }) =>
    api.put<Pipeline>(`${base}/types/${typeId}/pipelines/${id}`, body),
  removeTypePipeline: (typeId: string, id: string) => api.delete(`${base}/types/${typeId}/pipelines/${id}`),
  orderTypePipelines: (typeId: string, ids: string[]) =>
    api.put<Pipeline[]>(`${base}/types/${typeId}/pipelines/order`, { ids }),
  conversion: (id: string, signal?: AbortSignal) =>
    api.get<ConversionPreview>(item('lead', id, '/conversion'), { signal }),
  convert: (id: string, body: ConvertInput) => api.post<ConversionResult>(item('lead', id, '/convert'), body),
  timeline: (type: EntityType, id: string, cursor: string | null, signal?: AbortSignal) =>
    api.get<CursorPage<TimelineEntry>>(item(type, id, '/timeline'), { query: { cursor, limit: 20 }, signal }),
  addComment: (type: EntityType, id: string, body: { bodyHtml: string; attachmentIds: string[] }) =>
    api.post<TimelineEntry>(item(type, id, '/comments'), body),
  updateComment: (commentId: string, body: { bodyHtml: string; attachmentIds: string[] }) =>
    api.put<CrmComment>(`${base}/comments/${encodeURIComponent(commentId)}`, body),
  removeComment: (commentId: string) => api.delete(`${base}/comments/${encodeURIComponent(commentId)}`),
  activities: (query: ListQuery, signal?: AbortSignal) =>
    api.get<PagedResult<Activity>>(`${base}/activities`, { query, signal }),
  counters: (todayEnd: string, signal?: AbortSignal) =>
    api.get<ActivityCounters>(`${base}/activities/counters`, { query: { todayEnd }, signal }),
  createActivity: (body: Record<string, unknown>) => api.post<Activity>(`${base}/activities`, body),
  updateActivity: (id: string, body: Record<string, unknown>) =>
    api.patch<Activity>(`${base}/activities/${encodeURIComponent(id)}`, body),
  completeActivity: (id: string, completed: boolean) =>
    api.post<Activity>(`${base}/activities/${encodeURIComponent(id)}/complete`, { completed }),
  removeActivity: (id: string) => api.delete(`${base}/activities/${encodeURIComponent(id)}`),
  lookup: (type: EntityType, search: string, signal?: AbortSignal, typeId?: string) =>
    api.get<RecordRef[]>(`${base}/lookup`, { query: { type, search, limit: 10, typeId }, signal }),
  duplicates: (type: 'lead' | 'contact' | 'company', query: ListQuery, signal?: AbortSignal) =>
    api.get<{ items: Duplicate[]; hidden: number }>(`${base}/duplicates`, {
      query: { type, ...query },
      signal,
    }),
  filters: (type: EntityType, typeId?: string | null, signal?: AbortSignal) =>
    api.get<SavedFilter[]>(`${base}/filters`, { query: { entityType: type, typeId: typeId ?? undefined }, signal }),
  saveFilter: (body: {
    entityType: EntityType;
    name: string;
    query: Record<string, string>;
    typeId?: string | null;
    scope?: ViewScope;
    columns?: string[] | null;
    defaultRole?: string | null;
  }) => api.post<SavedFilter>(`${base}/filters`, body),
  removeFilter: (id: string) => api.delete(`${base}/filters/${encodeURIComponent(id)}`),
  summary: (userId: string, from: string, signal?: AbortSignal) =>
    api.get<UserSummary>(`${base}/users/${encodeURIComponent(userId)}/summary`, { query: { from }, signal }),

  settings: (signal?: AbortSignal) => api.get<CrmSettings>(`${base}/settings`, { signal }),
  updateIntake: (body: {
    mode: IntakeMode;
    responsibleId: string | null;
    sources: Partial<Record<IntakeSourceKind, string>>;
  }) => api.put<CrmSettings>(`${base}/settings/intake`, body),
  currencyRates: (signal?: AbortSignal) => api.get<CurrencyRates>(`${base}/currency-rates`, { signal }),
  updateCurrencyRates: (body: { provider: RatesProvider; autoUpdate: boolean }) =>
    api.put<CurrencyRates>(`${base}/currency-rates`, body),
  refreshCurrencyRates: () => api.post<CurrencyRates>(`${base}/currency-rates/refresh`, {}),
  currencyHistory: (code: string, signal?: AbortSignal) =>
    api.get<CurrencyRateHistory[]>(`${base}/currencies/${encodeURIComponent(code)}/history`, {
      query: { days: 180 },
      signal,
    }),
  updateSettings: (body: { defaultRole?: CrmRole; feedOnDealWon?: boolean; useLeads?: boolean }) =>
    api.put<CrmSettings>(`${base}/settings`, body),
  createPipeline: (name: string) => api.post<Pipeline>(`${base}/pipelines`, { name }),
  updatePipeline: (id: string, body: { name: string; isDefault?: boolean }) =>
    api.put<Pipeline>(`${base}/pipelines/${id}`, body),
  removePipeline: (id: string) => api.delete(`${base}/pipelines/${id}`),
  orderPipelines: (ids: string[]) => api.put<Pipeline[]>(`${base}/pipelines/order`, { ids }),
  addStage: (pipelineId: string, body: { name: string; color?: string; semantic?: StageSemantic }) =>
    api.post<Pipeline>(`${base}/pipelines/${pipelineId}/stages`, body),
  updateStage: (id: string, body: { name: string; color?: string; requiredFields?: string[] }) =>
    api.put<Pipeline>(`${base}/stages/${id}`, body),
  setPipelineAccess: (
    id: string,
    rows: { role: CrmRole; canView: boolean; canEdit: boolean; canDelete: boolean }[],
  ) => api.put<PipelineAccess[]>(`${base}/pipelines/${id}/access`, { rows }),
  trash: (query: ListQuery, signal?: AbortSignal) => api.get<TrashPage>(`${base}/trash`, { query, signal }),
  restore: (type: EntityType, id: string) =>
    api.post<RestoreResult>(`${base}/trash/${type}/${encodeURIComponent(id)}/restore`, {}),
  purge: (type: EntityType, id: string) =>
    api.delete<{ purged: number }>(`${base}/trash/${type}/${encodeURIComponent(id)}`),
  emptyTrash: (type?: EntityType) =>
    api.delete<{ purged: number }>(`${base}/trash`, { query: { type } }),
  removeStage: (id: string) => api.delete<Pipeline>(`${base}/stages/${id}`),
  orderStages: (pipelineId: string, ids: string[]) =>
    api.put<Pipeline>(`${base}/pipelines/${pipelineId}/stages/order`, { ids }),
  addStatus: (body: { name: string; color?: string; semantic?: LeadStatusSemantic }) =>
    api.post<LeadStatus[]>(`${base}/lead-statuses`, body),
  updateStatus: (id: string, body: { name: string; color?: string }) =>
    api.put<LeadStatus[]>(`${base}/lead-statuses/${id}`, body),
  removeStatus: (id: string) => api.delete<LeadStatus[]>(`${base}/lead-statuses/${id}`),
  orderStatuses: (ids: string[]) => api.put<LeadStatus[]>(`${base}/lead-statuses/order`, { ids }),
  addItem: (list: 'sources' | 'loss-reasons', name: string) =>
    api.post<ListItem[]>(`${base}/${list}`, { name }),
  updateItem: (list: 'sources' | 'loss-reasons', id: string, name: string) =>
    api.put<ListItem[]>(`${base}/${list}/${id}`, { name }),
  removeItem: (list: 'sources' | 'loss-reasons', id: string) =>
    api.delete<ListItem[]>(`${base}/${list}/${id}`),
  orderItems: (list: 'sources' | 'loss-reasons', ids: string[]) =>
    api.put<ListItem[]>(`${base}/${list}/order`, { ids }),
  setScopes: (role: 'viewer' | 'manager', body: Omit<RoleScopes, 'role'>) =>
    api.put<CrmSettings>(`${base}/roles/${role}`, body),
  assign: (userId: string, role: CrmRole) => api.put<CrmSettings>(`${base}/access/${userId}`, { role }),
  unassign: (userId: string) => api.delete<CrmSettings>(`${base}/access/${userId}`),
  updateDocumentSettings: (body: {
    quotePrefix?: string;
    invoicePrefix?: string;
    invoicePaidWinsDeal?: boolean;
    seller?: Record<string, string>;
  }) => api.put<CrmSettings>(`${base}/settings`, body),

  addCurrency: (code: string, rate: number) => api.post<Currency[]>(`${base}/currencies`, { code, rate }),
  setRate: (code: string, rate: number) => api.put<Currency[]>(`${base}/currencies/${code}`, { rate }),
  makeBase: (code: string) => api.put<Currency[]>(`${base}/currencies/${code}/base`, {}),
  removeCurrency: (code: string) => api.delete<Currency[]>(`${base}/currencies/${code}`),
  orderCurrencies: (codes: string[]) => api.put<Currency[]>(`${base}/currencies/order`, { codes }),

  createField: (body: Record<string, unknown>) => api.post<CustomField[]>(`${base}/custom-fields`, body),
  updateField: (id: string, body: Record<string, unknown>) =>
    api.put<CustomField[]>(`${base}/custom-fields/${id}`, body),
  removeField: (id: string) => api.delete<CustomField[]>(`${base}/custom-fields/${id}`),
  orderFields: (entityType: EntityType, ids: string[], dynamicTypeId?: string) =>
    api.put<CustomField[]>(`${base}/custom-fields/order`, { entityType, ids, dynamicTypeId }),

  sections: (signal?: AbortSignal) => api.get<ProductSection[]>(`${base}/product-sections`, { signal }),
  createSection: (body: { name: string; parentId?: string | null }) =>
    api.post<ProductSection[]>(`${base}/product-sections`, body),
  updateSection: (id: string, body: { name: string; parentId?: string | null }) =>
    api.put<ProductSection[]>(`${base}/product-sections/${id}`, body),
  removeSection: (id: string) => api.delete<ProductSection[]>(`${base}/product-sections/${id}`),
  products: (query: ListQuery, signal?: AbortSignal) =>
    api.get<PagedResult<Product>>(`${base}/products`, { query, signal }),
  product: (id: string, signal?: AbortSignal) =>
    api.get<Product>(`${base}/products/${encodeURIComponent(id)}`, { signal }),
  createProduct: (body: Record<string, unknown>) => api.post<Product>(`${base}/products`, body),
  updateProduct: (id: string, body: Record<string, unknown>) =>
    api.patch<Product>(`${base}/products/${encodeURIComponent(id)}`, body),
  removeProduct: (id: string) => api.delete(`${base}/products/${encodeURIComponent(id)}`),
  rows: (type: EntityType, id: string, signal?: AbortSignal) =>
    api.get<ProductRows>(item(type, id, '/products'), { signal }),
  saveRows: (type: EntityType, id: string, rows: ProductRowInput[]) =>
    api.put<ProductRows>(item(type, id, '/products'), { rows }),

  setDocumentStatus: (type: DocumentType, id: string, status: string) =>
    api.post<DocumentDetail>(item(type, id, '/status'), { status }),
  dashboard: (query: ListQuery, signal?: AbortSignal) =>
    api.get<Dashboard>(`${base}/dashboard`, { query, signal }),
  duplicateGroups: (type: 'contact' | 'company', signal?: AbortSignal) =>
    api.get<{ groups: DuplicateGroup[]; hidden: number }>(`${base}/duplicates/groups`, {
      query: { type },
      signal,
    }),
  merge: <T>(
    type: 'contact' | 'company',
    body: { targetId: string; sourceIds: string[]; values?: Record<string, string> },
  ) => api.post<T>(`${base}/${ROUTE[type]}/merge`, body),
  importPreview: (body: Record<string, unknown>) => api.post<ImportPreview>(`${base}/imports/preview`, body),
  startImport: (body: Record<string, unknown>) => api.post<ImportJob>(`${base}/imports`, body),
  imports: (signal?: AbortSignal) => api.get<ImportJob[]>(`${base}/imports`, { signal }),
  importJob: (id: string, signal?: AbortSignal) =>
    api.get<ImportJob>(`${base}/imports/${encodeURIComponent(id)}`, { signal }),
  /** The current list view as a file (`format`: csv or xlsx); `truncated` when the list had more rows than the file. */
  export: async (type: EntityType, query: ListQuery, format: 'csv' | 'xlsx') => {
    let truncated = false;
    const body = await api.get<Blob | string>(`${base}/${ROUTE[type]}/export`, {
      query: { ...query, page: undefined, pageSize: undefined, format },
      headers: { Accept: '*/*' },
      onResponse: (response) => {
        truncated = response.headers.get('X-Orbita-Truncated') === 'true';
      },
    });
    return { body, truncated };
  },
};

/* ------------------------------------------------------------------ hooks */

export function useTrash(query: ListQuery, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: crmKeys.trash(query),
    queryFn: ({ signal }) => crmApi.trash(query, signal),
    placeholderData: keepPreviousData,
    enabled: options.enabled ?? true,
  });
}

export function useCrmMe(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: crmKeys.me,
    queryFn: ({ signal }) => crmApi.me(signal),
    staleTime: 5 * 60_000,
    enabled: options.enabled ?? true,
  });
}

export function useRecordList<T>(type: EntityType, query: ListQuery, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: crmKeys.list(type, query),
    queryFn: ({ signal }) => crmApi.list<T>(type, query, signal),
    placeholderData: keepPreviousData,
    enabled: options.enabled ?? true,
  });
}

export function useBoard<T>(type: 'lead' | 'deal' | 'item', query: ListQuery, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: crmKeys.board(type, query),
    queryFn: ({ signal }) => crmApi.board<T>(type, query, signal),
    placeholderData: keepPreviousData,
    enabled: options.enabled ?? true,
  });
}

export function useRecord<T>(type: EntityType, id: string | null) {
  return useQuery({
    queryKey: crmKeys.detail(type, id ?? ''),
    queryFn: ({ signal }) => crmApi.get<T>(type, id ?? '', signal),
    enabled: !!id,
  });
}

export function useStageHistory(id: string, enabled = true) {
  return useQuery({
    queryKey: crmKeys.stageHistory(id),
    queryFn: ({ signal }) => crmApi.stageHistory(id, signal),
    enabled,
  });
}

export function useItemHistory(id: string, enabled = true) {
  return useQuery({
    queryKey: crmKeys.itemHistory(id),
    queryFn: ({ signal }) => crmApi.itemHistory(id, signal),
    enabled,
  });
}

/** The items of user-defined types linked to a lead or a deal. */
export function useLinkedItems(kind: 'lead' | 'deal', id: string, enabled = true) {
  return useQuery({
    queryKey: crmKeys.linkedItems(kind, id),
    queryFn: ({ signal }) => crmApi.linkedItems(kind, id, signal),
    enabled,
  });
}

/** The companies linked to a company (the «Компании» section of its «Связи» tab). */
export function useCompanyLinks(id: string, enabled = true) {
  return useQuery({
    queryKey: crmKeys.companyLinks(id),
    queryFn: ({ signal }) => crmApi.companyLinks(id, signal),
    enabled,
  });
}

/** A link between companies changed: both lists, both timelines and the company views. */
export function invalidateCompanyLinks(queryClient: QueryClient, companyIds: readonly string[]): void {
  void queryClient.invalidateQueries({ queryKey: crmKeys.companyLinksAll });
  for (const id of companyIds) void queryClient.invalidateQueries({ queryKey: crmKeys.timeline('company', id) });
  invalidateViews(queryClient, 'company');
}

/** The types with their counts (administrators' settings). */
export function useDynamicTypes(enabled = true) {
  return useQuery({ queryKey: crmKeys.types, queryFn: ({ signal }) => crmApi.types(signal), enabled });
}

export function useActivities(query: ListQuery, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: crmKeys.activityList(query),
    queryFn: ({ signal }) => crmApi.activities(query, signal),
    placeholderData: keepPreviousData,
    enabled: options.enabled ?? true,
  });
}

/** End of the local day (counters of "today"). */
export function endOfToday(now = new Date()): string {
  const end = new Date(now);
  end.setHours(24, 0, 0, 0);
  return end.toISOString();
}

export function useActivityCounters(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: crmKeys.counters,
    queryFn: ({ signal }) => crmApi.counters(endOfToday(), signal),
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
    enabled: options.enabled ?? true,
  });
}

export function useSavedFilters(type: EntityType, enabled = true, typeId?: string | null) {
  return useQuery({
    queryKey: crmKeys.filters(type, typeId),
    queryFn: ({ signal }) => crmApi.filters(type, typeId, signal),
    staleTime: 5 * 60_000,
    enabled,
  });
}

export function useCrmSettings(enabled = true) {
  return useQuery({ queryKey: crmKeys.settings, queryFn: ({ signal }) => crmApi.settings(signal), enabled });
}

export function useCurrencyRates(enabled = true) {
  return useQuery({
    queryKey: crmKeys.currencyRates,
    queryFn: ({ signal }) => crmApi.currencyRates(signal),
    enabled,
  });
}

export function useCurrencyHistory(code: string | null) {
  return useQuery({
    queryKey: crmKeys.currencyHistory(code ?? ''),
    queryFn: ({ signal }) => crmApi.currencyHistory(code ?? '', signal),
    enabled: !!code,
  });
}

export function useProductSections(enabled = true) {
  return useQuery({
    queryKey: crmKeys.sections,
    queryFn: ({ signal }) => crmApi.sections(signal),
    staleTime: 60_000,
    enabled,
  });
}

export function useProducts(query: ListQuery, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: crmKeys.productList(query),
    queryFn: ({ signal }) => crmApi.products(query, signal),
    placeholderData: keepPreviousData,
    enabled: options.enabled ?? true,
  });
}

export function useProductRows(type: EntityType, id: string, enabled = true) {
  return useQuery({
    queryKey: crmKeys.rows(type, id),
    queryFn: ({ signal }) => crmApi.rows(type, id, signal),
    enabled,
  });
}

export function useDashboard(query: ListQuery, enabled = true) {
  return useQuery({
    queryKey: crmKeys.dashboard(query),
    queryFn: ({ signal }) => crmApi.dashboard(query, signal),
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useDuplicateGroups(type: 'contact' | 'company') {
  return useQuery({
    queryKey: crmKeys.duplicateGroups(type),
    queryFn: ({ signal }) => crmApi.duplicateGroups(type, signal),
  });
}

export function useImports(enabled = true) {
  return useQuery({ queryKey: crmKeys.imports, queryFn: ({ signal }) => crmApi.imports(signal), enabled });
}

export function useImportJob(id: string | null) {
  return useQuery({
    queryKey: crmKeys.importJob(id ?? ''),
    queryFn: ({ signal }) => crmApi.importJob(id ?? '', signal),
    enabled: !!id,
    refetchInterval: (query) =>
      query.state.data && (query.state.data.status === 'completed' || query.state.data.status === 'failed')
        ? false
        : 3000,
  });
}

/** The start of the current month in the viewer's calendar, with its UTC offset (`2026-10-01T00:00:00+03:00`). */
export function startOfMonthLocal(now = new Date()): string {
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const offset = -start.getTimezoneOffset();
  const sign = offset >= 0 ? '+' : '-';
  const pad = (n: number) => String(Math.trunc(Math.abs(n))).padStart(2, '0');
  return `${start.getFullYear()}-${pad(start.getMonth() + 1)}-01T00:00:00${sign}${pad(offset / 60)}:${pad(offset % 60)}`;
}

export function useUserSummary(userId: string, enabled = true) {
  const from = startOfMonthLocal();
  return useQuery({
    queryKey: [...crmKeys.summary(userId), from],
    queryFn: ({ signal }) => crmApi.summary(userId, from, signal),
    enabled,
  });
}

/** Inline edit of a record: the card is replaced by the server's copy; views refresh. */
export function useUpdateRecord<T extends { id: string }>(type: EntityType, id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => crmApi.update<T>(type, id, body),
    onSuccess: (record) => {
      queryClient.setQueryData(crmKeys.detail(type, id), record);
      void queryClient.invalidateQueries({ queryKey: crmKeys.timeline(type, id) });
      // A new currency converts the product rows too.
      void queryClient.invalidateQueries({ queryKey: crmKeys.rows(type, id) });
      invalidateViews(queryClient, type);
    },
  });
}

export function useCreateRecord<T extends { id: string }>(type: EntityType) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => crmApi.create<T>(type, body),
    meta: { silent: true },
    onSuccess: (record) => {
      queryClient.setQueryData(crmKeys.detail(type, record.id), record);
      invalidateViews(queryClient, type);
    },
  });
}

export function useDeleteRecord(type: EntityType) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => crmApi.remove(type, id),
    onSuccess: (_result, id) => {
      queryClient.removeQueries({ queryKey: crmKeys.detail(type, id) });
      invalidateViews(queryClient, type);
      void queryClient.invalidateQueries({ queryKey: crmKeys.activities });
      void queryClient.invalidateQueries({ queryKey: crmKeys.trashAll });
    },
  });
}

/** Activity mutations refresh the activity lists, counters and the owner's card and timeline. */
export function invalidateActivities(
  queryClient: QueryClient,
  owner?: { type: EntityType; id: string },
): void {
  void queryClient.invalidateQueries({ queryKey: crmKeys.activities });
  void queryClient.invalidateQueries({ queryKey: ['crm', 'summary'] });
  if (owner) {
    void queryClient.invalidateQueries({ queryKey: crmKeys.detail(owner.type, owner.id) });
    void queryClient.invalidateQueries({ queryKey: crmKeys.timeline(owner.type, owner.id) });
    invalidateViews(queryClient, owner.type);
  }
}
